import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';

import { useDb, useEnsureAuth } from '../firebase/FirebaseContext';
import {
  V1_PREVIEW_MEMORY_KEY,
  previewParamValue,
  resolvePreviewOverride,
} from './previewMemory';

/**
 * THE-955: the combined-QA master switch for V1 (THE-946 PR1/PR4/PR5 — the
 * board progress card, the gift-guide rail, and the desktop calendar). It is
 * NOT a fourth feature flag that replaces the three independent ones; the
 * board explicitly kept per-feature flags on THE-947/THE-950/THE-951
 * (2026-10-13) rather than adopting one shared `boardV1` flag. This module
 * exists so the combined QA pass (PRD PR1 line: "on the preview route, all
 * together") has one tester-facing switch instead of three separately-carried
 * query params.
 *
 *   v1PreviewActive = ?v1Preview=1                  -> true   (query override)
 *                     : ?v1Preview=0                -> false  (override off)
 *                     : remembered from an earlier ?v1Preview=1 in this
 *                         browser                   -> true   (previewMemory.ts)
 *                     : featureFlags/v1Preview.enabled ?? false
 *
 * A COMPOSITION INPUT, NOT A FOURTH GATE. Nothing should render on
 * `useV1PreviewEnabled()` alone — that would let a surface honor the master
 * switch while forgetting its own per-feature flag, the exact disagreement
 * `boardGuideRailFlag.ts` already rules out between the rail and the guide
 * back-link. Instead, each of the three per-feature flags' own override check
 * ORs in `hasV1PreviewOverride`, and its own release-switch hook ORs in
 * `useV1PreviewReleased`, e.g.:
 *
 *   hasBoardGuideRailOverride(search) ===
 *     search.get('boardGuideRail') === '1' || hasV1PreviewOverride(search)
 *
 * `board/boardGuideRailFlag.ts` carries this composition today. PR1's
 * `boardProgressCardFlag.ts` (THE-947) and PR5's `calendarRedesignFlag.ts`
 * (THE-951) need the identical one-line addition to their own override check
 * and release hook before those PRs merge — called out on THE-947 and
 * THE-951 rather than wired here, because neither file exists on `master`
 * yet.
 *
 * `useV1PreviewEnabled` below is for a QA-only "V1 preview mode" indicator
 * (e.g. a banner), not a substitute for checking the per-feature flag a given
 * surface actually owns.
 *
 * FAIL-CLOSED, mirroring `boardGuideRailFlag.ts`. V1 ships by code change
 * (removing the gate), never by flipping this Firestore doc — so no rules
 * block for `featureFlags/v1Preview` is coming (THE-946, 2026-10-13). The
 * read below is PERMANENTLY permission-denied in production, not waiting on
 * a merge, and the catch is written to resolve `false` silently rather than
 * throw or log, since that denial is now the expected steady state. The
 * query override is the only live door.
 *
 * RELEASED 2026-11-02 (the Gift Guide Hub + Fresh Find Fridays launch, Kate's
 * plan step 1: "turn the V1 pieces on by default, since v1Preview is meant to
 * ship by code change; `?v1Preview=0` stays as an off switch"). That code
 * change is `V1_RELEASED` below. It is the general-release answer the
 * (denied) Firestore read could never give:
 *
 *   isV1Released(search) = V1_RELEASED && search is not `?v1Preview=0`
 *
 * It feeds ONLY the release half of each consumer — `useV1PreviewReleased`,
 * `useV1PreviewReleaseState`, and the two synchronous readers that had no
 * release half at all (the accent recolor, the phone band placement). The
 * OVERRIDE half (`hasV1PreviewOverride`, the carriers) is untouched on
 * purpose: carriers stamp `?v1Preview=1` onto in-app links only for a tester
 * who arrived with it, so organic URLs stay clean now that the param is
 * redundant. `?v1Preview=0` beats the release (an off switch per page load,
 * which is all a rollback-without-deploy needs; a hosting rollback is the real
 * one) — but, as before, it does NOT beat a per-feature door such as
 * `?boardProgressCard=1`, which is that feature's own cohort decision.
 */

/**
 * V1 is released by code. Flip to `false` (and deploy) to put every V1 piece
 * back behind its tester door at once; delete the constant, the
 * `isV1Released` short-circuits, and the per-feature preview tables once the
 * V1 look is simply "the look" and nothing needs the old branch.
 */
export const V1_RELEASED = true;

/** Query param carrying the combined-QA cohort override. */
export const V1_PREVIEW_PARAM = 'v1Preview';

/** The value that activates the override (and, from a landing URL, remembers
 *  it in this browser). `=0` turns it off and forgets it; any other value
 *  defers to that memory. */
export const V1_PREVIEW_PARAM_ON = '1';

/**
 * Firestore collection for the general-release switch. NOT imported from
 * `board/boardGuideRailFlag`'s identical `FEATURE_FLAGS_COLLECTION` — that
 * file imports from this one for the override composition below, and the
 * reverse import would be a cycle. Keep this literal equal to that one's.
 */
export const FEATURE_FLAGS_COLLECTION = 'featureFlags';

/** Firestore location of the general-release switch (server-written). */
export const V1_PREVIEW_FLAG_DOC = 'v1Preview';

/** Shape of `featureFlags/v1Preview`. */
export interface V1PreviewFlagDoc {
  enabled?: boolean;
}

/**
 * Is the combined-QA override on for this location? `?v1Preview=1` -> true,
 * `?v1Preview=0` -> false, otherwise whatever this browser remembers from an
 * earlier tester link (`previewMemory.ts`) — so a bare `search` can be true.
 * Synchronous and Router-free, matching `hasBoardGuideRailOverride`, so
 * per-feature flag modules can call it without spending a render.
 */
export function hasV1PreviewOverride(search: string): boolean {
  return resolvePreviewOverride(search, V1_PREVIEW_PARAM, V1_PREVIEW_MEMORY_KEY);
}

/**
 * Is V1 released for this location? `V1_RELEASED`, unless the URL says
 * `?v1Preview=0`. Synchronous and Router-free (pure on `search`), like
 * `hasV1PreviewOverride`, so module-scope readers can call it.
 */
export function isV1Released(search: string): boolean {
  return V1_RELEASED && previewParamValue(search, V1_PREVIEW_PARAM) !== false;
}

/**
 * The resolved V1 switch for a SYNCHRONOUS consumer: the tester override OR
 * the code release. The hooks below answer the same question for a component
 * that also wants the Firestore read's (denied) opinion.
 */
export function isV1Active(search: string): boolean {
  return hasV1PreviewOverride(search) || isV1Released(search);
}

/**
 * Append `?v1Preview=1` to an in-app path when the master switch is active —
 * the master switch's own carrier, matching `withBoardGuideRailParam` and
 * `withDesktopCalendarParam`'s shape so every cohort carrier composes the
 * same way (H10/H11, THE-581).
 *
 * THE-955 GAP THIS CLOSES: a tester who reached a board via `?v1Preview=1`
 * previously lost it on the next hop, because every per-feature carrier
 * (`withBoardGuideRailParam`, etc.) only re-attaches ITS OWN query param, not
 * this one. Each call site that chains those carriers has to chain this one
 * too, keyed on `hasV1PreviewOverride(location.search)`, the same way it
 * already chains the gift-guide-hub and hint-pill carriers.
 *
 * String surgery on a relative router path rather than `new URL()` (which
 * needs a base), preserving any existing query and hash. Idempotent —
 * `set()`, not `append()`.
 */
export function withV1PreviewParam(path: string, active: boolean): string {
  if (!active || !path) return path;
  const hashAt = path.indexOf('#');
  const hash = hashAt === -1 ? '' : path.slice(hashAt);
  const beforeHash = hashAt === -1 ? path : path.slice(0, hashAt);
  const queryAt = beforeHash.indexOf('?');
  const pathname = queryAt === -1 ? beforeHash : beforeHash.slice(0, queryAt);
  const search = queryAt === -1 ? '' : beforeHash.slice(queryAt + 1);
  const params = new URLSearchParams(search);
  params.set(V1_PREVIEW_PARAM, V1_PREVIEW_PARAM_ON);
  return `${pathname}?${params.toString()}${hash}`;
}

/**
 * The general-release switch alone, so a per-feature flag's own release hook
 * can OR it in without also paying for a second query-string read (the
 * override is checked once, by the caller).
 */
export function useV1PreviewReleased(skip: boolean): boolean {
  const { search } = useLocation();
  // Released by code (2026-11-02): the Firestore read below could only agree,
  // so skip it — it is permission-denied in production anyway.
  const released = isV1Released(search);
  const db = useDb();
  const ensureAuth = useEnsureAuth();
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (skip || released) return undefined;
    let cancelled = false;

    (async () => {
      try {
        await ensureAuth();
        const snap = await getDoc(
          doc(db, FEATURE_FLAGS_COLLECTION, V1_PREVIEW_FLAG_DOC),
        );
        if (cancelled || !snap.exists()) return;
        const data = snap.data() as V1PreviewFlagDoc;
        // `=== true`, not truthiness — a typo'd string in the flag doc must
        // not switch on every V1 piece at once.
        if (data?.enabled === true) setEnabled(true);
      } catch {
        // Denied (no rules block exists or ever will for V1), offline, or
        // absent — all mean "not released". Deliberately silent: this read
        // is PERMANENTLY expected to fail in production.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [db, ensureAuth, skip, released]);

  // `skip` means "this caller must not be released here" (a direct /guide/*
  // visit), not just "don't spend a read" — the code release honors it too.
  return !skip && (released || enabled);
}

/** The release read's answer AND whether it has answered yet. */
export interface V1PreviewReleaseState {
  enabled: boolean;
  /** True once the read has settled (allowed, denied, absent) or was skipped. */
  settled: boolean;
}

const SKIPPED_RELEASE_STATE: V1PreviewReleaseState = { enabled: false, settled: true };
/** Released by code: the answer is known before any read, so it is settled. */
const CODE_RELEASED_STATE: V1PreviewReleaseState = { enabled: true, settled: true };

/**
 * `useV1PreviewReleased` plus whether its read has SETTLED, for a caller that
 * must not act on the provisional `false` while the read is in flight (the
 * desktop calendar's one-shot scroll anchor). Same read, same fail-closed
 * rules; kept as a sibling rather than folded into `useV1PreviewReleased` so
 * the board flags, which only need the answer, don't pick up an extra
 * re-render when the (permanently denied) read settles. A skipped read counts
 * as settled — the caller already knows the answer.
 */
export function useV1PreviewReleaseState(skip: boolean): V1PreviewReleaseState {
  const { search } = useLocation();
  // Released by code (2026-11-02) — same short-circuit as `useV1PreviewReleased`.
  const released = isV1Released(search);
  const db = useDb();
  const ensureAuth = useEnsureAuth();
  const [state, setState] = useState<V1PreviewReleaseState>({
    enabled: false,
    settled: false,
  });

  useEffect(() => {
    if (skip || released) return undefined;
    let cancelled = false;

    (async () => {
      let enabled = false;
      try {
        await ensureAuth();
        const snap = await getDoc(
          doc(db, FEATURE_FLAGS_COLLECTION, V1_PREVIEW_FLAG_DOC),
        );
        // `=== true`, as above.
        enabled =
          snap.exists() && (snap.data() as V1PreviewFlagDoc)?.enabled === true;
      } catch {
        // Denied / offline / absent: "not released", settled. Silent, as above.
      }
      // Same object when nothing changed: a re-run of this effect must not
      // re-render (and so re-run) forever.
      if (!cancelled) {
        setState((prev) =>
          prev.settled && prev.enabled === enabled ? prev : { enabled, settled: true },
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [db, ensureAuth, skip, released]);

  if (skip) return SKIPPED_RELEASE_STATE;
  return released ? CODE_RELEASED_STATE : state;
}

export interface UseV1PreviewEnabledOptions {
  /** Skip the release-switch read entirely — see `useBoardGuideRailEnabled`'s
   *  identical option for why a caller that already knows the answer is
   *  irrelevant should take this rather than spend a Firestore read. */
  skip?: boolean;
}

/**
 * The resolved master switch, for a QA-only indicator — NOT for gating a V1
 * feature surface itself (see the module doc above). Reactive on the URL,
 * matching `useBoardGuideRailEnabled`.
 */
export function useV1PreviewEnabled(
  options: UseV1PreviewEnabledOptions = {},
): boolean {
  const { search } = useLocation();
  const override = hasV1PreviewOverride(search);
  const released = useV1PreviewReleased(options.skip === true || override);
  return override || released;
}
